import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuthContext } from "../auth/AuthContext";
import { DataSelectionContext } from "./DataSelectionContext";
import { useBoundarySelectContext } from "../boundarySelection/BoundarySelectContext";
import { useWfsService } from "../../hooks";
import { BoundarySource } from "../../constant/BoundarySource";
import { WfsGetFeatureRequest } from "../../types/Wfs/WfsGetFeatureRequest";
import {
  WfsBadRequestResponse,
  WfsDataResponse,
  WfsDeferredRequestResponse,
  WfsDuplicateOrderResponse,
  WfsPaymentRequiredResponse,
  WfsResponseType,
} from "../../types/Wfs/WfsResponse";
import WKT from "ol/format/WKT";
import { useCurrencyContext } from "../../context/Currency/CurrencyContext";
import useBasketService from "../../hooks/useBasketService";
import { useBasketContext } from "../../context/Basket/BasketContext";
import { useLayerContext } from "../../context/Layer/LayerContext";

export interface DataSelectionProps {
  children: React.ReactNode;
}

const DataSelectionProvider = ({ children }: DataSelectionProps) => {
  const location = useLocation();
  const { layers } = useLayerContext();
  const wfsService = useWfsService();
  const { boundaries } = useBoundarySelectContext();
  const { selectedCurrency } = useCurrencyContext();
  const basketService = useBasketService();
  const { setBasket } = useBasketContext();
  const navigate = useNavigate();

  const wktFormater = useMemo(() => new WKT(), []);
  const [wfsResult, setWfsResultInternal] = useState<
    | WfsDataResponse
    | WfsDeferredRequestResponse
    | WfsPaymentRequiredResponse
    | WfsBadRequestResponse
    | WfsDuplicateOrderResponse
    | undefined
  >(undefined);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setWfsResultInternal(undefined);
    setLoading(false);
  }, [location]);

  const getLayer = (layerName: string) => {
    const layer = layers.find(
      (l) => l.name.toLocaleLowerCase() === layerName.toLocaleLowerCase(),
    );

    return layer;
  };

  const wfsOrder = (requestParameters: WfsGetFeatureRequest) => {
    setLoading(true);
    wfsService
      .WfsPost({
        ...requestParameters,
        withinBoundary: boundaries
          .filter((b) => b.source === BoundarySource.KnownBoundary)
          .map((b) =>
            (b.bufferDistanceMetres ?? 0) > 0 ?
              `${b.boundaryId.toString()} ${b.bufferDistanceMetres}`
            : b.boundaryId.toString(),
          ),
        wkt: boundaries
          .filter((b) => b.source !== BoundarySource.KnownBoundary)
          .map((b) => `SRID=3857;${wktFormater.writeGeometry(b.geometry)}`),
        currencyCode: selectedCurrency?.code,
      })
      .then((response) => {
        setWfsResultInternal(response);
      })
      .catch(() => {})
      .finally(() => {
        setLoading(false);
      });
  };

  const addToBasket = (id: string) => {
    setLoading(true);
    basketService
      .AddToBasket(id)
      .then((basket) => {
        setBasket(basket);
        navigate("/basket");
      })
      .catch((e) => {
        if (e.response.status === 409) {
          const duplicateOrder = {
            type: WfsResponseType.DuplicateOrder,
            orderId: e.response.data.orderId,
            orderItemId: e.response.data.orderItemId,
            isInBasket: e.response.data.isInBasket,
          };
          setWfsResultInternal(duplicateOrder);
        }
      })
      .finally(() => {
        setLoading(false);
      });
  };

  return (
    <DataSelectionContext.Provider
      value={{
        layers,
        getLayer,
        hasBoundaries: !!boundaries.length,
        wfsOrder,
        addToBasket,
        wfsResult,
        wfsReset: () => setWfsResultInternal(undefined),
        loading,
      }}
    >
      {children}
    </DataSelectionContext.Provider>
  );
};

export default DataSelectionProvider;
